import { useState, useEffect, useCallback } from "react";
import {
  AuthenticationDetails,
  CognitoUserSession,
  CognitoUserAttribute,
  ISignUpResult,
  CognitoUser,
  ICognitoUserPoolData,
  CognitoRefreshToken,
} from "amazon-cognito-identity-js";
import userPool from "../../utils/userPool";
import { refreshToken, removeToken, setToken } from "../../utils/token";
import { useCompanyStore } from "../../state/useCompanyStore";
import { useUserStore } from "../../state";
import {
  STORAGE_AUTH_USER,
  STORAGE_REFRESH_TOKEN,
  STORAGE_TOKEN,
  STORAGE_USER_DATA,
} from "../../constants";
import { checkTokenExpiration } from "../../utils";

interface UseCognitoReturn {
  user: CognitoUser | null;
  session: CognitoUserSession | null;
  loading: boolean;
  error: string | null;
  login: (username: string, password: string) => Promise<CognitoUserSession>;
  logout: () => void;
  register: (
    username: string,
    password: string,
    attributes: CognitoUserAttribute[],
  ) => Promise<ISignUpResult>;
  forgotPassword: (username: string) => Promise<void>;
  confirmPassword: (
    username: string,
    verificationCode: string,
    newPassword: string,
  ) => Promise<void>;
  // refreshSession: () => Promise<CognitoUserSession>;
}

const useCognito = (): UseCognitoReturn => {
  const [user, setUser] = useState<CognitoUser | null>(null);
  const { cognitoUser, setCognitoUser, session, setSession } = useUserStore();
  // const [session, setSession] = useState<CognitoUserSession | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [lastVerificationCodeSent, setLastVerificationCodeSent] = useState<
    number | null
  >(null);

  const login = useCallback(
    (username: string, password: string): Promise<CognitoUserSession> => {
      setLoading(true);
      setError(null);

      return new Promise<CognitoUserSession>((resolve, reject) => {
        const authenticationDetails = new AuthenticationDetails({
          Username: username,
          Password: password,
        });

        const cognitoUser = new CognitoUser({
          Username: username,
          Pool: userPool,
        });

        cognitoUser.authenticateUser(authenticationDetails, {
          onSuccess: (session: CognitoUserSession) => {
            setUser(cognitoUser);
            setCognitoUser(cognitoUser);
            setSession(session);
            setLoading(false);
            const token = session.getIdToken().getJwtToken();
            setToken(token);

            localStorage.setItem(STORAGE_AUTH_USER, username);
            localStorage.setItem(
              STORAGE_REFRESH_TOKEN,
              session.getRefreshToken().getToken(),
            );
            refreshToken();
            resolve(session);
          },
          onFailure: (err: Error) => {
            console.error("Login failed:", err);
            setError(err.message || JSON.stringify(err));
            setLoading(false);
            reject(err);
          },
        });
      });
    },
    [],
  );

  const logout = useCallback((): void => {
    const currentUser = userPool.getCurrentUser();
    if (currentUser) {
      currentUser.signOut();
      setUser(null);
      setCognitoUser(null);
      setSession(null);
      removeToken();
    }

    localStorage.removeItem(STORAGE_AUTH_USER);
    localStorage.removeItem(STORAGE_REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_USER_DATA);
  }, []);

  const register = useCallback(
    (
      username: string,
      password: string,
      attributes: CognitoUserAttribute[],
    ): Promise<ISignUpResult> => {
      setLoading(true);
      setError(null);

      return new Promise<ISignUpResult>((resolve, reject) => {
        userPool.signUp(
          username,
          password,
          attributes,
          null as unknown as CognitoUserAttribute[],
          // @ts-ignore
          (err: Error | null, result?: ISignUpResult) => {
            setLoading(false);
            if (err) {
              console.error("Registration failed:", err);
              setError(err.message || JSON.stringify(err));
              reject(err);
            } else if (result) {
              resolve(result);
            } else {
              const unexpectedError = new Error(
                "Unexpected error during registration.",
              );
              console.error(unexpectedError);
              setError(unexpectedError.message);
              reject(unexpectedError);
            }
          },
        );
      });
    },
    [],
  );

  const forgotPassword = useCallback((username: string): Promise<void> => {
    setLoading(true);
    setError(null);

    return new Promise<void>((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
      });

      cognitoUser.forgotPassword({
        onSuccess: () => {
          setLoading(false);
          setLastVerificationCodeSent(Date.now());
          resolve();
        },
        onFailure: (err: Error) => {
          console.error("Forgot password failed:", err);
          setError(err.message || JSON.stringify(err));
          setLoading(false);
          reject(err);
        },
      });
    });
  }, []);

  const confirmPassword = useCallback(
    (
      username: string,
      verificationCode: string,
      newPassword: string,
    ): Promise<void> => {
      setLoading(true);
      setError(null);

      if (
        lastVerificationCodeSent &&
        Date.now() - lastVerificationCodeSent > 300000
      ) {
        const error = new Error(
          "Verification code expired or is not valid anymore.",
        );
        setError(error.message);
        setLoading(false);
        return Promise.reject(error);
      }

      return new Promise<void>((resolve, reject) => {
        const cognitoUser = new CognitoUser({
          Username: username,
          Pool: userPool,
        });

        cognitoUser.confirmPassword(verificationCode, newPassword, {
          onSuccess: () => {
            setLoading(false);
            resolve();
          },
          onFailure: (err: Error) => {
            console.error("Confirm password failed:", err);
            setError(err.message || JSON.stringify(err));
            setLoading(false);
            reject(err);
          },
        });
      });
    },
    [lastVerificationCodeSent],
  );

  const refreshSession = useCallback((): Promise<CognitoUserSession> => {
    if (!cognitoUser) {
      const noUserError = new Error("No user is currently logged in.");
      console.warn(noUserError.message);
      setError(noUserError.message);
      return Promise.reject(noUserError);
    }

    return new Promise<CognitoUserSession>((resolve, reject) => {
      cognitoUser.getSession(
        (err: Error | null, currentSession: CognitoUserSession | null) => {
          if (err || !currentSession) {
            const sessionError = err || new Error("No session available.");
            console.error("Error getting current session:", sessionError);
            setError(sessionError.message || JSON.stringify(sessionError));
            return reject(sessionError);
          }

          if (currentSession.isValid()) {
            console.log("Session is still valid. Returning current session.");
            setSession(currentSession);
            setToken(currentSession.getIdToken().getJwtToken());
            return resolve(currentSession);
          }

          console.log("Session expired. Attempting to refresh...");

          cognitoUser.refreshSession(
            currentSession.getRefreshToken(),
            (
              refreshErr: Error | null,
              refreshedSession: CognitoUserSession | null,
            ) => {
              if (refreshErr || !refreshedSession) {
                const refreshError =
                  refreshErr || new Error("Failed to refresh session.");
                console.error("Failed to refresh session:", refreshError);
                setError(refreshError.message || JSON.stringify(refreshError));
                return reject(refreshError);
              }

              console.log("Session refreshed successfully.");
              setSession(refreshedSession);
              setToken(refreshedSession.getIdToken().getJwtToken());

              // Optional: persist refresh token again, though usually it's unchanged
              localStorage.setItem(
                STORAGE_REFRESH_TOKEN,
                refreshedSession.getRefreshToken().getToken(),
              );

              return resolve(refreshedSession);
            },
          );
        },
      );
    });
  }, [cognitoUser]);

  useEffect(() => {
    // Check if we have stored authentication
    const storedUsername = localStorage.getItem(STORAGE_AUTH_USER);
    const storedRefreshToken = localStorage.getItem(STORAGE_REFRESH_TOKEN);

    if (storedUsername && storedRefreshToken) {
      // Recreate the Cognito user
      const userData = {
        Username: storedUsername,
        Pool: userPool,
      };

      const storedUser = new CognitoUser(userData);
      const refreshToken = new CognitoRefreshToken({
        RefreshToken: storedRefreshToken,
      });

      // Try to get a new session using the refresh token
      storedUser.refreshSession(refreshToken, (err, session) => {
        if (err) {
          console.error("Failed to restore session:", err);
          localStorage.removeItem(STORAGE_AUTH_USER);
          localStorage.removeItem(STORAGE_REFRESH_TOKEN);
          // Optionally redirect to login
        } else {
          // Successfully restored the session
          setUser(storedUser);
          setCognitoUser(storedUser);
          setSession(session);
          const token = session.getIdToken().getJwtToken();
          setToken(token);

          // Update stored refresh token with the new one
          localStorage.setItem(
            STORAGE_REFRESH_TOKEN,
            session.getRefreshToken().getToken(),
          );
        }
      });
    }
  }, []);

  useEffect(() => {
    let refreshTimer: NodeJS.Timeout;

    const scheduleRefresh = (currentSession: CognitoUserSession) => {
      const expiresAt = currentSession.getAccessToken().getExpiration() * 1000;
      const now = Date.now();
      const timeout = expiresAt - now - 5 * 60 * 1000; // Refresh 5 minutes before expiration

      if (timeout > 0) {
        refreshTimer = setTimeout(() => {
          refreshSession()
            .then((newSession) => {
              scheduleRefresh(newSession);
            })
            .catch((err) => {
              console.error("Failed to refresh session:", err);
              logout();
            });
        }, timeout);
      } else {
        refreshSession()
          .then((newSession) => {
            scheduleRefresh(newSession);
          })
          .catch((err) => {
            console.error("Failed to refresh session:", err);
            logout();
          });
      }
    };

    if (session) {
      scheduleRefresh(session);
    } else {
      const currentUser = userPool.getCurrentUser();
      if (currentUser) {
        currentUser.getSession(
          (err: Error | null, currentSession: CognitoUserSession | null) => {
            if (err) {
              console.error("Error initializing session:", err);
              logout();
            } else if (currentSession) {
              setUser(currentUser);
              setSession(currentSession);
              const token = currentSession.getIdToken().getJwtToken();
              setToken(token);
              scheduleRefresh(currentSession);
            }
          },
        );
      }
    }

    return () => {
      if (refreshTimer) {
        clearTimeout(refreshTimer);
      }
    };
  }, [session, refreshSession, logout]);

  const tokenRefresh = ({
    cognitoUser,
    session,
  }: {
    cognitoUser: CognitoUser;
    session: CognitoUserSession;
  }) => {
    if (!cognitoUser || !session) {
      console.error("No active user or session");
      return Promise.reject("No active session");
    }

    // Create a function that manually triggers the refresh flow
    return new Promise((resolve, reject) => {
      cognitoUser.refreshSession(
        session.getRefreshToken(),
        (refreshErr, refreshedSession) => {
          if (refreshErr) {
            console.error("Failed to refresh session:", refreshErr);
            reject(refreshErr);
          } else if (refreshedSession) {
            console.log("Session successfully refreshed!");
            setSession(refreshedSession);
            checkTokenExpiration(refreshedSession);
            const token = refreshedSession.getIdToken().getJwtToken();
            setToken(token);

            // Store the new refresh token
            localStorage.setItem(
              STORAGE_REFRESH_TOKEN,
              refreshedSession.getRefreshToken().getToken(),
            );

            // Log token expiration details
            const accessExpiration = refreshedSession
              .getAccessToken()
              .getExpiration();
            const idExpiration = refreshedSession.getIdToken().getExpiration();
            console.log(
              `New access token expires: ${new Date(accessExpiration * 1000).toLocaleString()}`,
            );
            console.log(
              `New ID token expires: ${new Date(idExpiration * 1000).toLocaleString()}`,
            );

            resolve(refreshedSession);
          } else {
            reject(
              new Error("Failed to refresh session - no session returned"),
            );
          }
        },
      );
    });
  };

  return {
    user: cognitoUser,
    session,
    loading,
    error,
    login,
    logout,
    register,
    forgotPassword,
    confirmPassword,
    // refreshSession,
  };
};

export default useCognito;
